import { TrashIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCreateAddress } from '@/api/addresses/mutations/useCreateAddress'
import { useDeleteAddress } from '@/api/addresses/mutations/useDeleteAddress'
import { useSetDefaultAddress } from '@/api/addresses/mutations/useSetDefaultAddress'
import { useUpdateAddress } from '@/api/addresses/mutations/useUpdateAddress'
import { useCities } from '@/api/addresses/queries/useCities'
import { useStreetsByCityId } from '@/api/addresses/queries/useStreetsByCityId'
import type { Address } from '@/api/addresses/types/Address'
import type { PlaceCity } from '@/api/addresses/types/Place'
import { AppDialog } from '@/components/AppDialog'
import { useConfirm } from '@/components/ConfirmDialog'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { stringSimilarity } from '@/lib/utils'

import { AutoCompleteField } from '../../../../components/AutoCompleteField'

type AddressFormData = {
  street: string
  apartment: string
  city: string
  postalCode: string
  note: string
  house: string
}

const defaultValues: AddressFormData = {
  street: '',
  apartment: '',
  city: '',
  postalCode: '',
  note: '',
  house: '',
}

// Pattern to check if street is only numbers (moshav pattern from marketing site)
const moshavStreetsPattern = /^\d+$/

interface EditAddressDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  editingAddress: Address | { userId: string } | null
  onSuccess?: (createdAddress?: Address) => void
}

export default function EditAddressDialog({ isOpen, onOpenChange, editingAddress, onSuccess }: EditAddressDialogProps) {
  const { t } = useTranslation()
  const { mutateAsync: createAddress, isPending: isCreatingAddress } = useCreateAddress()
  const { mutateAsync: updateAddress, isPending: isUpdatingAddress } = useUpdateAddress(editingAddress?.userId)
  const { mutateAsync: deleteAddressMutate, isPending: isDeletingAddress } = useDeleteAddress()
  const { mutateAsync: setDefaultAddress } = useSetDefaultAddress()
  const [isDefault, setIsDefault] = useState(false)
  const [selectedCity, setSelectedCity] = useState<PlaceCity | null>(null)
  const [selectedStreet, setSelectedStreet] = useState<string | null>(null)
  const { data: cities = [], isLoading: citiesLoading } = useCities()
  const streetsEnabled = selectedCity?.hasStreets ?? false
  const { data: streets = [], isLoading: streetsLoading } = useStreetsByCityId(selectedCity?.cityId ?? null, streetsEnabled)
  const { confirm: confirmDelete, ConfirmDialog: ConfirmDeleteDialog } = useConfirm()
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<AddressFormData>({
    defaultValues,
    mode: 'onBlur',
  })

  const cityValue = watch('city')
  const streetValue = watch('street')

  // When editing, resolve selectedCity from city name once cities are loaded
  useEffect(() => {
    if (!editingAddress || !('city' in editingAddress) || !editingAddress.city || cities.length === 0) return
    const match = cities.find(c => c.city.trim() === String(editingAddress.city).trim())
    if (match) setSelectedCity(match)
  }, [editingAddress, cities])

  useEffect(() => {
    if (editingAddress && 'id' in editingAddress) {
      reset({
        ...editingAddress,
        apartment: editingAddress.apartment ?? '',
        note: editingAddress.note ?? '',
        house: editingAddress.house ?? '',
      })
      setIsDefault(editingAddress.isDefault)
    } else {
      reset(defaultValues)
      setIsDefault(false)
      setSelectedCity(null)
    }
  }, [editingAddress, isOpen, reset])

  const isEditingExisting = Boolean(editingAddress && 'id' in editingAddress)
  const isMutationBusy = isCreatingAddress || isUpdatingAddress || isDeletingAddress

  const filteredCities = useMemo(() => {
    if (!cityValue?.trim()) return cities.sort((a, b) => a.city.localeCompare(b.city))
    const q = cityValue.trim()
    return cities
      .map(c => {
        const baseScore = stringSimilarity(q, c.city)
        const prefixMatch = c.city.trim().startsWith(q)
        const score = prefixMatch ? Math.max(baseScore, 0.99) : baseScore
        return { city: c, score }
      })
      .filter(({ score }) => score >= 0.25)
      .sort((a, b) => b.score - a.score)
      .map(({ city }) => city)
  }, [cities, cityValue])

  const filteredStreets = useMemo(() => {
    if (!streetValue?.trim()) return streets
    const q = streetValue.trim()
    return streets
      .map(s => ({ street: s, score: stringSimilarity(q, s.street) }))
      .filter(({ score }) => score >= 0.25)
      .sort((a, b) => b.score - a.score)
      .map(({ street }) => street)
  }, [streets, streetValue])

  const onCitySelect = useCallback(
    (city: PlaceCity) => {
      setValue('city', city.city)
      setSelectedCity(city)
      if (!city.hasStreets) setValue('street', '')
    },
    [setValue]
  )

  const onStreetSelect = useCallback(
    (option: { street: string }) => {
      setValue('street', option.street)
      setSelectedStreet(option.street)
    },
    [setValue]
  )

  const onSubmit = async (data: AddressFormData) => {
    if (!selectedCity) {
      setError('city', { message: t('profile.selectCityFromList') })
      return
    }
    if (streetsEnabled && !selectedStreet) {
      setError('street', { message: t('profile.selectStreetFromList') })
      return
    }
    try {
      if (editingAddress && 'id' in editingAddress) {
        await updateAddress({
          addressId: editingAddress.id,
          address: data,
        })

        if (isDefault && !editingAddress.isDefault) {
          await setDefaultAddress(editingAddress.id)
        }

        onOpenChange(false)
        reset(defaultValues)
        setIsDefault(false)
        setSelectedCity(null)
        onSuccess?.()
      } else {
        const createdAddress = await createAddress({ ...data, userId: editingAddress?.userId })

        if (isDefault) {
          await setDefaultAddress(createdAddress.id)
        }

        onOpenChange(false)
        reset(defaultValues)
        setIsDefault(false)
        setSelectedCity(null)
        onSuccess?.(createdAddress)
      }
    } catch (error) {
      console.error('Failed to save address:', error)
    }
  }

  const handleConfirmDelete = async () => {
    if (!editingAddress || !('id' in editingAddress)) return
    const isConfirmed = await confirmDelete({
      title: t('profile.deleteAddressConfirmTitle'),
      description: t('profile.deleteAddressConfirmMessage'),
      confirmLabel: t('profile.deleteAddressConfirm'),
      cancelLabel: t('common.cancel'),
    })
    if (!isConfirmed) return
    try {
      await deleteAddressMutate(editingAddress.id)
      onOpenChange(false)
      reset(defaultValues)
      setIsDefault(false)
      setSelectedCity(null)
      setSelectedStreet(null)
      onSuccess?.()
    } catch (error) {
      console.error('Failed to delete address:', error)
    }
  }

  return (
    <AppDialog
      isOpen={isOpen}
      dialogContentClassName='pt-6'
      onOpenChange={onOpenChange}
      onOpenAutoFocus={e => e.preventDefault()}
      title={
        <span className='flex items-center justify-between gap-2'>
          {editingAddress ? t('profile.editAddressDialog') : t('profile.addNewAddress')}

          {isEditingExisting && (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              disabled={isMutationBusy}
              onClick={handleConfirmDelete}
              className='text-red-600 hover:bg-red-50 hover:text-red-700'
            >
              <TrashIcon className='size-4' />
            </Button>
          )}
        </span>
      }
    >
      <form className='flex flex-col items-end gap-8'>
        <div className='flex w-full flex-col gap-[21px]'>
          {/* City autocomplete */}
          <Controller
            name='city'
            control={control}
            rules={{ required: t('profile.requiredField') }}
            render={({ field }) => (
              <AutoCompleteField<PlaceCity>
                id='address-city'
                label={t('profile.city')}
                value={field.value}
                onChange={v => {
                  field.onChange(v)
                  setSelectedCity(null)
                }}
                onSelect={onCitySelect}
                options={filteredCities}
                getOptionLabel={c => c.city}
                loading={citiesLoading}
                error={!!errors.city}
                supportingText={errors.city?.message}
                className='w-full'
              />
            )}
          />

          {/* Street: autocomplete when city has streets, otherwise free text */}
          {selectedCity?.hasStreets ? (
            <Controller
              name='street'
              control={control}
              rules={{
                required: t('profile.requiredField'),
              }}
              render={({ field }) => (
                <AutoCompleteField<{ street: string }>
                  id='address-street'
                  label={t('profile.street')}
                  value={field.value}
                  onChange={v => {
                    field.onChange(v)
                    setSelectedStreet(null)
                  }}
                  onSelect={onStreetSelect}
                  options={filteredStreets}
                  getOptionLabel={s => s.street}
                  loading={streetsLoading}
                  error={!!errors.street}
                  supportingText={errors.street?.message}
                  className='w-full'
                />
              )}
            />
          ) : (
            <Controller
              name='street'
              control={control}
              rules={{
                required: t('profile.requiredField'),
                maxLength: {
                  value: 25,
                  message: t('profile.streetMaxLength'),
                },
                validate: value => {
                  if (moshavStreetsPattern.test(value ?? '')) {
                    return t('profile.houseNumbersOnly')
                  }
                  return true
                },
              }}
              render={({ field }) => (
                <TextField
                  id='address-street'
                  label={t('profile.street')}
                  {...field}
                  error={!!errors.street}
                  supportingText={errors.street?.message}
                />
              )}
            />
          )}

          <div className='flex w-full gap-4'>
            <Controller
              name='house'
              control={control}
              rules={{
                required: selectedCity?.hasStreets ? t('profile.requiredField') : false,
              }}
              render={({ field }) => (
                <TextField
                  id='address-house'
                  label={t('profile.houseNumber')}
                  {...field}
                  error={!!errors.house}
                  supportingText={errors.house?.message}
                  className='flex-1'
                />
              )}
            />
            <Controller
              name='apartment'
              control={control}
              render={({ field }) => (
                <TextField id='address-apartment' label={t('profile.apartmentNumber')} {...field} className='flex-1' />
              )}
            />
            <Controller
              name='postalCode'
              control={control}
              rules={{
                required: t('profile.requiredField'),
                validate: value => {
                  if (!value) {
                    return true
                  }
                  if (!/^\d+$/.test(value)) {
                    return t('profile.postalCodeNumbersOnly')
                  }
                  if (value.length !== 7) {
                    return t('profile.postalCodeLength')
                  }
                  return true
                },
              }}
              render={({ field }) => (
                <TextField
                  id='address-postalcode'
                  label={t('profile.postalCode')}
                  {...field}
                  error={!!errors.postalCode}
                  supportingText={errors.postalCode?.message}
                  className='flex-1'
                />
              )}
            />
          </div>
          <Controller
            name='note'
            control={control}
            render={({ field }) => <TextField id='address-note' label={t('profile.addressNote')} {...field} />}
          />
          <div className='flex items-center gap-2'>
            <Checkbox id='set-default' checked={isDefault} onCheckedChange={checked => setIsDefault(checked === true)} />
            <label htmlFor='set-default' className='text-text-secondary cursor-pointer text-sm' dir='rtl'>
              {t('profile.setAsDefaultShipping')}
            </label>
          </div>
        </div>
        <div className='flex w-full flex-col items-center gap-4'>
          <Button
            onClick={handleSubmit(onSubmit)}
            type='button'
            size='lg'
            loading={isCreatingAddress || isUpdatingAddress}
            disabled={isMutationBusy}
            className='w-full max-w-[335px]'
          >
            {editingAddress ? t('profile.saveChanges') : t('profile.saveNewAddress')}
          </Button>
        </div>
      </form>
      {ConfirmDeleteDialog}
    </AppDialog>
  )
}
